import React, {ReactElement} from 'react';
import styled, {css} from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {InlineLinkButton} from '../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {renderInlineList} from '../../../../helpers/listHelpers';
import {getCellBackgroundColor, getCellIndexStyles, getCellTextColor} from '../helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../tableColumn';
import {TableCell, TableCellProps, TableCellStyleProps} from './tableCell';

/*
 * Props.
 */

interface HandleCellProps extends TableCellProps<string[]> {
  hasChannel: boolean;
  onComposeMessage: HandlerOf<string>;
}

/*
 * Style.
 */

const StyledTextCellContainerDiv = styled.div<TableCellStyleProps>`
  padding: 0 12px;
  display: flex;
  overflow: hidden;
  white-space: nowrap;
  align-items: center;
  height: 100%;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )}
`;

interface StyledTextCellValueDivProps {
  $isSelected: boolean;
}
const StyledTextCellValueDiv = styled.div<StyledTextCellValueDivProps>`
  line-height: 16px;
  ${ellipsis()};
  ${(p) => getCellTextColor(p.$isSelected)}
`;

interface StyledHandleSpanProps {
  $isSelected: boolean;
}
const StyledHandleSpan = styled.span<StyledHandleSpanProps>`
  white-space: nowrap;
  text-overflow: ellipsis;
  word-break: break-all;
  ${(p) => getCellTextColor(p.$isSelected)}
`;

interface StyledInlineLinkButtonProps {
  $isSelected: boolean;
}
const StyledInlineLinkButton = styled(InlineLinkButton)<StyledInlineLinkButtonProps>`
  ${(p) =>
    p.$isSelected &&
    css`
      color: ${p.theme.greys.white};
    `}
`;

/*
 * Component.
 */

export const HandleCell = (props: HandleCellProps): ReactElement => {
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    shouldShowStickyGradient,
    hasChannel,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
    onComposeMessage,
  } = props;

  return (
    <TableCell<string[]>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderCell={(value) => (
        <StyledTextCellContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledTextCellValueDiv $isSelected={Boolean(isSelected)}>
            {renderInlineList(
              renderHandles(value, hasChannel, Boolean(isSelected), onComposeMessage),
              undefined,
              false,
            )}
          </StyledTextCellValueDiv>
        </StyledTextCellContainerDiv>
      )}
    />
  );
};

/*
 * Helpers.
 */

const renderHandles = (
  handles: string[],
  hasChannel: boolean,
  isSelected: boolean,
  onComposeMessage: HandlerOf<string>,
) =>
  handles.map((handle) => {
    if (hasChannel) {
      return (
        <StyledInlineLinkButton
          key={handle}
          $isSelected={isSelected}
          onClick={(event) => {
            event.preventDefault();
            onComposeMessage(handle);
          }}
        >
          {handle}
        </StyledInlineLinkButton>
      );
    }
    return (
      <StyledHandleSpan key={handle} $isSelected={isSelected}>
        {handle}
      </StyledHandleSpan>
    );
  });
